import React from 'react';
import PropTypes from 'prop-types';
import { LuFile, LuFileText, LuImage, LuPlus, LuReceipt, LuRefreshCw } from 'react-icons/lu';
import Button from '../../../../components/ui/Button';
import { useTranslation } from '../../../../i18n/LanguageContext';
import ExpenseModal from '../../ExpenseModal';
import { EXPENSE_TYPE_STYLES } from '../expenseTypes';
import { EmptyBlock, PANEL } from '../VisitUi';

const attachmentIcon = (type) => {
  if (type?.startsWith('image/')) return LuImage;
  if (type === 'application/pdf') return LuFileText;
  return LuFile;
};

const amount = (cost) => (Number(cost) || 0).toFixed(2);

/** "Harcamalar": the visit's expenses, added through ExpenseModal (written at once). */
const ExpensesStep = ({ expenses, isReadOnly, visitId, showExpenseModal, onOpenAdd, onCloseAdd, onRefresh, onExpenseAdded }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`visitModal.expenses.${key}`, vars);
  const total = expenses.reduce((sum, expense) => sum + (Number(expense.cost) || 0), 0);

  const typeBadge = (type) => {
    const style = EXPENSE_TYPE_STYLES[type];
    if (!style) return null;
    const Icon = style.icon;
    return (
      <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset ${style.tone}`}>
        <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {label(`types.${type}`)}
      </span>
    );
  };

  const attachments = (expense) => (
    expense.attachments && expense.attachments.length > 0 ? (
      <div className="tw-flex tw-flex-wrap tw-gap-1.5">
        {expense.attachments.map((attachment, attachIndex) => {
          const Icon = attachmentIcon(attachment.type);
          return (
            <button
              key={attachIndex}
              type="button"
              onClick={() => window.open(attachment.url, '_blank')}
              title={`${attachment.name} - ${attachment.size ? Math.round(attachment.size / 1024) + ' KB' : ''}`}
              className="tw-inline-flex tw-max-w-[11rem] tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/80 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-sky-700 tw-transition-colors hover:tw-border-sky-400/60 hover:tw-bg-sky-50 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-sky-500/40 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-sky-300 dark:hover:tw-bg-white/[0.08]"
            >
              <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              <span className="tw-truncate">{attachment.name || label('fileFallback')}</span>
            </button>
          );
        })}
      </div>
    ) : <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>
  );

  return (
    <div className="tw-space-y-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-3">
          <span className="tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-xl tw-bg-amber-500/10 tw-text-amber-600 tw-ring-1 tw-ring-inset tw-ring-amber-500/25 dark:tw-text-amber-300">
            <LuReceipt aria-hidden="true" className="tw-h-5 tw-w-5" />
          </span>
          <div>
            <p className="tw-text-xs tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
              {label('total')} · {label('count', { count: expenses.length })}
            </p>
            <p className="tw-text-lg tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{label('amount', { value: amount(total) })}</p>
          </div>
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2 max-sm:tw-w-full">
          <Button onClick={onRefresh} disabled={isReadOnly} title={label('refreshHint')} className="max-sm:tw-flex-1">
            <LuRefreshCw aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('refresh')}
          </Button>
          <Button variant="primary" onClick={onOpenAdd} disabled={isReadOnly} className="max-sm:tw-flex-1">
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('add')}
          </Button>
        </div>
      </div>

      {expenses.length === 0 ? (
        <EmptyBlock icon={LuReceipt} title={label('emptyTitle')} text={label('emptyText')} />
      ) : (
        <>
          {/* Phones: one card per expense */}
          <ul className="tw-space-y-2 md:tw-hidden">
            {expenses.map((expense, index) => (
              <li key={index} className={`${PANEL} tw-space-y-2 tw-p-3.5`}>
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                  <div className="tw-min-w-0">
                    <p className="tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{expense.name}</p>
                    <p className="tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{new Date(expense.createdAt).toLocaleDateString(locale)}</p>
                  </div>
                  <span className="tw-shrink-0 tw-text-sm tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{label('amount', { value: amount(expense.cost) })}</span>
                </div>
                {typeBadge(expense.type)}
                {expense.information && <p className="tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">{expense.information}</p>}
                {attachments(expense)}
              </li>
            ))}
          </ul>

          {/* Tablets and up: a table */}
          <div className={`${PANEL} tw-hidden tw-overflow-x-auto md:tw-block glass-scrollbar`}>
            <table className="tw-w-full tw-min-w-[44rem] tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-border-b tw-border-slate-200/80 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-border-white/10 dark:tw-text-slate-400">
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.name')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.type')}</th>
                  <th className="tw-px-4 tw-py-3 tw-text-right tw-font-semibold">{label('columns.cost')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.info')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.files')}</th>
                  <th className="tw-px-4 tw-py-3 tw-font-semibold">{label('columns.date')}</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense, index) => (
                  <tr key={index} className="tw-border-b tw-border-slate-100 tw-align-top last:tw-border-0 dark:tw-border-white/[0.06]">
                    <td className="tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-900 dark:tw-text-white">{expense.name}</td>
                    <td className="tw-px-4 tw-py-3">{typeBadge(expense.type)}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{label('amount', { value: amount(expense.cost) })}</td>
                    <td className="tw-px-4 tw-py-3 tw-text-slate-600 dark:tw-text-slate-300">{expense.information || '-'}</td>
                    <td className="tw-px-4 tw-py-3">{attachments(expense)}</td>
                    <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-slate-500 dark:tw-text-slate-400">{new Date(expense.createdAt).toLocaleDateString(locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ExpenseModal show={showExpenseModal} onHide={onCloseAdd} visitId={visitId} onExpenseAdded={onExpenseAdded} />
    </div>
  );
};

ExpensesStep.propTypes = {
  expenses: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool,
  visitId: PropTypes.string,
  showExpenseModal: PropTypes.bool.isRequired,
  onOpenAdd: PropTypes.func.isRequired,
  onCloseAdd: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onExpenseAdded: PropTypes.func.isRequired,
};

export default ExpensesStep;
